import { Controller } from "@hotwired/stimulus"
import {
  vendorAllSorted, vendorGet, vendorAdd, vendorUpdate, vendorDelete, vendorSearch,
} from "vendor_library"
import { libAll } from "goal_library"
import {
  vendorEditorHtml, vendorListRowHtml, locationRowHtml, esc,
} from "vendor_editor_view"

// Settings → Vendors manager: lists the user's LOCAL vendor library and lets
// them add / edit / delete vendors. The library is stored locally (PCPDesk:
// OneDrive-synced SQLite via window.__PCP_LIBRARY__; plain browser:
// localStorage) — NOT on the server — so this reads/writes it client-side.
export default class extends Controller {
  static targets = ["list", "empty", "count", "editor", "addBtn", "search"]

  connect() {
    this.render()
    // Watch for section visibility changes (when left-rail nav switches sections).
    this._observer = new MutationObserver(() => {
      if (this.element.hidden && this.hasEditorTarget && !this.editorTarget.hidden) {
        this._closeEditor()
      }
    })
    this._observer.observe(this.element, { attributes: true, attributeFilter: ["hidden"] })
  }

  disconnect() {
    if (this._observer) this._observer.disconnect()
  }

  render() {
    const query = this.hasSearchTarget ? this.searchTarget.value.trim() : ""
    const vendors = query ? vendorSearch(query) : vendorAllSorted()
    if (this.hasCountTarget) {
      this.countTarget.textContent = vendors.length ? `${vendors.length} vendor${vendors.length === 1 ? "" : "s"}` : ""
    }
    if (this.hasEmptyTarget) this.emptyTarget.hidden = vendors.length > 0
    if (this.hasListTarget) this.listTarget.innerHTML = vendors.map(v => vendorListRowHtml(v, esc)).join("")
  }

  search() {
    this.render()
  }

  add() {
    this._openEditor({
      name: "", contactName: "", phone: "", email: "", website: "",
      serviceTypes: [], rate: { amount: "", unit: "per_hour" },
      locations: [], notes: "",
    }, null)
  }

  edit(event) {
    const id = event.currentTarget.dataset.id
    const v = vendorGet(id)
    if (v) this._openEditor(v, id)
  }

  remove(event) {
    const id = event.currentTarget.dataset.id
    const vendor = vendorGet(id)
    if (!vendor) return

    // Check if vendor is referenced by any goal in the goal library
    const goals = libAll()
    let refCount = 0
    for (const g of goals) {
      // Goals may have a vendors array in family data — check via libFamilyForSerial
      // but that's complex. For now, check if the vendor name appears in any goal's
      // family vendors (task 009 wiring will add the full cross-reference).
      // Simplified: we scan localStorage for references to this vendor id/name.
      // Since goal family data may reference vendors by id or name, we check both.
    }
    // For now, simple delete confirmation. Task 009 will wire the cross-reference.
    let msg = `Delete vendor "${vendor.name}"? This can't be undone.`

    // Check if vendor is in any goal's family vendor list (best-effort)
    try {
      const goalOverrides = JSON.parse(localStorage.getItem("pcp_goal_overrides") || "{}")
      for (const [key, val] of Object.entries(goalOverrides)) {
        if (key.startsWith("libfam/") && val && Array.isArray(val.vendors)) {
          for (const fv of val.vendors) {
            if (fv && (fv.id === id || fv.name === vendor.name)) {
              refCount++
              break
            }
          }
        }
      }
    } catch (_) { /* ignore */ }

    if (refCount > 0) {
      msg = `This vendor is referenced by ${refCount} goal${refCount === 1 ? "" : "s"} in your library.\n\n${msg}`
    }

    if (window.confirm(msg)) {
      vendorDelete(id)
      this.render()
    }
  }

  // ---- full-space inline editor ----

  _openEditor(v, id) {
    const isNew = !id

    // Locations need temp ids for DOM manipulation
    const locations = (v.locations || []).map((loc, i) => ({
      id: loc.id || `loc_${Date.now()}_${i}`,
      label: loc.label || "",
      address: loc.address || "",
      zip: loc.zip || "",
    }))

    // Collision-proof temp ids for new rows
    let rid = 1
    const nid = () => `loc_${Date.now().toString(36)}${(rid++).toString(36)}${Math.random().toString(36).slice(2, 6)}`

    // Generate HTML using the pure module
    const html = vendorEditorHtml({
      vendor: { ...v, locations },
      id,
    })

    // Hide list/empty/add button/search, show editor
    if (this.hasListTarget) this.listTarget.hidden = true
    if (this.hasEmptyTarget) this.emptyTarget.hidden = true
    if (this.hasAddBtnTarget) this.addBtnTarget.hidden = true
    if (this.hasSearchTarget) this.searchTarget.parentElement.hidden = true

    this.editorTarget.hidden = false
    this.editorTarget.innerHTML = html

    const editor = this.editorTarget.querySelector(".goal-editor")

    // --- location list editor ---
    const renderLocations = () => {
      const box = editor.querySelector('[data-list="locations"]')
      box.innerHTML = locations.length
        ? locations.map(locationRowHtml).join("")
        : `<div class="goal-editor__empty">No locations yet.</div>`
    }

    // Pull current DOM values back into the locations array
    const syncLocationsFromDom = () => {
      const box = editor.querySelector('[data-list="locations"]')
      box.querySelectorAll("[data-row]").forEach(row => {
        const item = locations.find(x => String(x.id) === String(row.dataset.row))
        if (!item) return
        row.querySelectorAll("[data-c]").forEach(inp => { item[inp.dataset.c] = inp.value })
      })
    }

    // Add location button
    editor.querySelector('[data-add="locations"]').addEventListener("click", () => {
      syncLocationsFromDom()
      locations.push({ id: nid(), label: "", address: "", zip: "" })
      renderLocations()
    })

    // Delete location buttons
    editor.addEventListener("click", (e) => {
      const del = e.target.closest('[data-del="locations"]')
      if (!del) return
      const row = del.closest("[data-row]")
      syncLocationsFromDom()
      const i = locations.findIndex(x => String(x.id) === String(row.dataset.row))
      if (i >= 0) locations.splice(i, 1)
      renderLocations()
    })

    // --- get service types from checkboxes ---
    const getServiceTypes = () => {
      const types = []
      editor.querySelectorAll("[data-svc]").forEach(cb => {
        if (cb.checked) types.push(cb.dataset.svc)
      })
      return types
    }

    // --- save / cancel ---
    const val = (n) => { const e = editor.querySelector(`[data-f="${n}"]`); return e ? e.value : "" }
    const close = () => {
      document.removeEventListener("keydown", onKey)
      this._closeEditor()
    }
    const onKey = (e) => { if (e.key === "Escape") { e.preventDefault(); close() } }
    document.addEventListener("keydown", onKey)

    editor.querySelector('[data-role="cancel"]').addEventListener("click", close)
    editor.querySelector('[data-role="save"]').addEventListener("click", () => {
      const name = val("name").trim()
      if (!name) {
        const n = editor.querySelector('[data-f="name"]')
        if (n) n.focus()
        return
      }

      syncLocationsFromDom()
      const cleanLocations = locations
        .map(loc => ({ label: loc.label.trim(), address: loc.address.trim(), zip: loc.zip.trim() }))
        .filter(loc => loc.label || loc.address || loc.zip)

      const fields = {
        name,
        contactName: val("contactName").trim(),
        phone: val("phone").trim(),
        email: val("email").trim(),
        website: val("website").trim(),
        serviceTypes: getServiceTypes(),
        rate: { amount: val("rateAmount").trim(), unit: val("rateUnit") },
        locations: cleanLocations,
        notes: val("notes"),
      }

      if (isNew) {
        vendorAdd(fields)
      } else {
        vendorUpdate(id, fields)
      }

      document.removeEventListener("keydown", onKey)
      this._closeEditor()
      this.render()
    })

    // Focus name input
    setTimeout(() => {
      const n = editor.querySelector('[data-f="name"]')
      if (n) { n.focus(); n.select() }
    }, 0)
  }

  _closeEditor() {
    if (this.hasEditorTarget) {
      this.editorTarget.hidden = true
      this.editorTarget.innerHTML = ""
    }
    if (this.hasListTarget) this.listTarget.hidden = false
    if (this.hasEmptyTarget) {
      const vendors = vendorAllSorted()
      this.emptyTarget.hidden = vendors.length > 0
    }
    if (this.hasAddBtnTarget) this.addBtnTarget.hidden = false
    if (this.hasSearchTarget) this.searchTarget.parentElement.hidden = false
  }
}
